/**
 * Pure helpers for hash-based navigation tabs (`NavTabs` with `href="#..."` links).
 *
 * In-page jump tabs scroll the element whose id matches the hash. These utilities normalize
 * `location.hash` and map it to a tab `value` so the active underline stays in
 * sync when users click tabs, use back/forward, or land on a deep link.
 *
 * `CATEGORY_HUB_TOP_ANCHOR_ID` is the shared DOM id / hash for the parent hub
 * "All" tab — used by publisher shortcodes and dynamic hub views so tab hrefs
 * and scroll targets stay aligned.
 */

/** Scroll target for the parent category hub page top / "All" jump tab. */
export const CATEGORY_HUB_TOP_ANCHOR_ID = 'category-hub-top';

export function normalizeLocationHash(hash: string): string {
  if (!hash) return '';
  const normalized = hash.startsWith('#') ? hash : `#${hash}`;
  return normalized.toLowerCase();
}

/** True when every tab href is an in-page hash link (e.g. `#section-id`). */
export function tabsUseHashHrefs(tabs: Array<{ href: string }>): boolean {
  return tabs.length > 0 && tabs.every((tab) => tab.href.startsWith('#'));
}

/** Match the current location hash to a tab href; default to the first tab when empty or unmatched. */
export function resolveHashNavActiveValue(
  tabs: Array<{ value: string; href: string }>,
  hash: string,
): string {
  const normalized = normalizeLocationHash(hash);
  if (!normalized) {
    return tabs[0]?.value ?? '';
  }

  const match = tabs.find(
    (tab) => normalizeLocationHash(tab.href) === normalized,
  );
  return match?.value ?? tabs[0]?.value ?? '';
}
